import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import BudgetModes from '@/common/enums/budget-modes.enum';
import Campaign from '@/common/models/campaign/Campaign';
import EntityProperty from '@/common/models/EntityProperty';
import LineItem from '@/common/models/lineitem/LineItem';
import { DASH, getStore, toServerDate } from '@/globals';
import rootStore from '@/store';
import {
  UPDATE_FLIGHT_ENDDATE,
  UPDATE_FLIGHT_GOAL,
  UPDATE_FLIGHT_STARTDATE,
} from '@/store/modules/models/mutation-types';
import { getDiff } from '@/utils/Diff';
import Validatable, { IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

import { FlightTableItem } from './FlightTableItem';

export interface IBaseFlightAPI {
  flight_id?: number;
}

interface IFlight {
  campaign?: Campaign;
  campaignId?: number;
  lineItem?: LineItem;
  lineItemId?: number;
  endDate: DateTime;
  goal: number;
  id?: number;
  startDate: DateTime;
}

export interface ILineItemFlightAPI extends IBaseFlightAPI {
  created_by: number;
  created_on_utc: string;
  lineitem_flight_goal: number;
  lineitem_flight_goal_ratio: number;
  lineitem_id: number;
  modified_by: number;
  modified_on_utc: string;
  flight: ICampaignFlightAPI;
}

export interface ICampaignFlightAPI extends IBaseFlightAPI {
  campaign_id?: number;
  end_date: string;
  goal: number;
  start_date: string;
}

export type IFlightAPI = ILineItemFlightAPI | ICampaignFlightAPI;

class Flight extends Validatable {
  public apiObject: IFlightAPI;

  constructor(
    public id: number,
    public startDate: DateTime,
    public endDate: DateTime,
    public goal: number,
    public campaignId?: number,
    public campaign?: Campaign,
    public lineItemId?: number,
    public lineItem?: LineItem,
  ) {
    super();

    this.apiObject = null;
  }

  public static get entityProperties() {
    const editableClassName = 'Flight';

    if (Flight.entityPropertiesCached) {
      return Flight.entityPropertiesCached;
    }

    const action = (flight: Flight) => {
      rootStore.dispatch('orgApp/updateCampaign', { instance: flight.campaign, showLoader: false });
    };
    const getExtraValidation = (flight: Flight) => {
      const extras: Record<string, IExtraValidation> = {};

      if (flight.startDate > flight.endDate) {
        extras.startDate = {
          message: i18n.global.t('messages.startDateBeforeEndDate'),
          preEvaluated: true,
        };
      }

      return extras;
    };

    Flight.entityPropertiesCached = {
      id: new EntityProperty({
        label: capitalize(i18n.global.t('names.id')),
        type: TableColumnTypes.integer,
        property: 'id',
      }),
      startDate: new EntityProperty({
        label: capitalize(i18n.global.t('names.startDate')),
        type: TableColumnTypes.date,
        property: 'startDate',
        editablePropertyName: 'startDate',
        editableClassName,
        mutation: UPDATE_FLIGHT_STARTDATE,
        isEditableHandler: (entity: FlightTableItem) => entity.flight.startDate > DateTime.now().startOf('day'),
        action,
        getExtraValidation,
      }),
      endDate: new EntityProperty({
        label: capitalize(i18n.global.t('names.endDate')),
        type: TableColumnTypes.date,
        property: 'endDate',
        editablePropertyName: 'endDate',
        editableClassName,
        mutation: UPDATE_FLIGHT_ENDDATE,
        isEditableHandler: (entity: FlightTableItem) => entity.flight.endDate > DateTime.now().startOf('day'),
        action,
        getExtraValidation,
      }),
      goal: new EntityProperty({
        label: capitalize(i18n.global.t('names.goal')),
        property: 'goal',
        editablePropertyName: 'goal',
        editableClassName,
        type: TableColumnTypes.integer,
        mutation: UPDATE_FLIGHT_GOAL,
        isEditableHandler: (entity: FlightTableItem) =>
          !entity.hasStarted || !entity.flight.isFlightRunning || entity.flight.endDate < DateTime.now().startOf('day'),
        action,
      }),
      adSpend: new EntityProperty({
        label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
        type: TableColumnTypes.currency,
      }),
      impressionDelivered: new EntityProperty({
        label: capitalize(i18n.global.t('names.impressionDelivered', { n: 1 })),
        type: TableColumnTypes.integer,
      }),
      cpm: new EntityProperty({
        label: capitalize(i18n.global.t('names.cpm')),
        type: TableColumnTypes.currency,
      }),
      pacingVsExpected: new EntityProperty({
        label: capitalize(i18n.global.t('messages.pacingVsExpected')),
        type: TableColumnTypes.percentage,
      }),
      pacingVsEven: new EntityProperty({
        label: capitalize(i18n.global.t('messages.pacingVsEven')),
        type: TableColumnTypes.percentage,
      }),
      progress: new EntityProperty({
        label: capitalize(i18n.global.t('names.progress', { n: 1 })),
        type: TableColumnTypes.percentage,
      }),
    };

    return Flight.entityPropertiesCached;
  }

  public get budgetMode(): BudgetModes {
    return this.campaign?.pacingBudgetMode || this.lineItem?.budgetMode || null;
  }

  public get startDateFormatted(): string {
    return this.startDate.toLocaleString(DateTime.DATE_SHORT);
  }

  public get endDateFormatted(): string {
    return this.endDate.toLocaleString(DateTime.DATE_SHORT);
  }

  public get goalFormatted(): string {
    return this.budgetMode === BudgetModes.BUDGET ? i18n.global.n(this.goal, 'currency') : i18n.global.n(this.goal);
  }

  public get isFlightRunning(): boolean {
    const zone = getStore().state?.orgApp?.organization?.timezone;
    const today = zone ? DateTime.local({ zone }).startOf('day') : DateTime.now().startOf('day');
    return this.startDate <= today && this.endDate >= today;
  }

  public get isFlightOver(): boolean {
    const zone = getStore().state?.orgApp?.organization?.timezone;
    const today = zone ? DateTime.local({ zone }).startOf('day') : DateTime.now().startOf('day');
    return this.startDate < today && this.endDate < today;
  }

  public get pacingVsEven(): string {
    return this.isFlightRunning ? (this.campaign?.pacingVsEvenFormatted ?? DASH) : DASH;
  }

  public get pacingVsExpected(): string {
    return this.isFlightRunning ? this.campaign?.pacingFormatted : DASH;
  }

  public get pacingVsEvenColorIndicatorClassName(): string {
    return this.campaign?.pacingVsEvenColorIndicatorClassName;
  }

  public get pacingColorIndicatorClassName(): string {
    return this.campaign?.pacingColorIndicatorClassName;
  }

  public get progress(): string {
    return this.isFlightRunning ? (this.campaign?.progressFormatted ?? DASH) : DASH;
  }

  public get hasStarted(): boolean {
    return this.campaign?.hasStarted ?? false;
  }

  public get flightDurationInDays(): number {
    return this.endDate.diff(this.startDate, 'days').days;
  }

  public get flightDescription() {
    return `${capitalize(i18n.global.t('names.flight'))}: ${this.startDateFormatted} - ${this.endDateFormatted}`;
  }

  public toApi(): ICampaignFlightAPI {
    const apiObject: ICampaignFlightAPI = {
      start_date: toServerDate(this.startDate),
      end_date: toServerDate(this.endDate),
      goal: this.goal,
    };

    if (this.id) {
      apiObject.flight_id = this.id;
    }

    return apiObject;
  }

  public static fromApi(apiObject: IFlightAPI, campaign: Campaign = null, lineItem: LineItem = null): Flight {
    let obj;

    if (lineItem) {
      obj = apiObject as ILineItemFlightAPI;
      return new Flight(
        obj.flight_id,
        DateTime.fromISO(obj.flight.start_date),
        DateTime.fromISO(obj.flight.end_date),
        obj.lineitem_flight_goal,
        null,
        campaign,
        obj.lineitem_id,
        lineItem,
      );
    }

    obj = apiObject as ICampaignFlightAPI;

    const flight = new Flight(
      obj.flight_id,
      DateTime.fromISO(obj.start_date),
      DateTime.fromISO(obj.end_date),
      obj.goal,
      obj.campaign_id,
      campaign,
    );

    flight.apiObject = apiObject;

    return flight;
  }

  public static fromObject(object: IFlight): Flight {
    return new Flight(
      object?.id || null,
      object.startDate,
      object.endDate,
      Number(object.goal),
      object?.campaignId || null,
      object?.campaign || null,
      object?.lineItemId || null,
      object?.lineItem || null,
    );
  }

  isPristine(): boolean {
    if (this.isNew) return true;

    const ignoreProperties = ['campaign_id', 'created_by', 'created_on_utc', 'modified_by', 'modified_on_utc'];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);

    return diffs.length === 0;
  }

  clone(): Flight {
    return Flight.fromObject({ ...this });
  }

  duplicate(newStartDate: DateTime, newEndDate: DateTime): Flight {
    return new Flight(-1, newStartDate, newEndDate, this.goal);
  }
}

export default Flight;
